'use client';

import { CSSProperties, useState, useRef, useEffect } from 'react';
import Webcam from 'react-webcam';
import clsx from 'clsx';

import { Text } from '@northladder/i18n';
import { TTextLocalized } from '@northladder/utilities';

import { translations } from '../../../translations';

import { CameraLoader } from '../../image-cropper';

import { requestCameraPermission } from '../cameraUtils';

interface SimpleAutoCameraProps {
  cameraAccessErrorMessage?: TTextLocalized;
  className?: string;
  classNameError?: string;
  facingMode?: 'front' | 'back';
  /**
   * The camera height, default to `932`
   */
  height?: number;
  /**
   * Whether to render the camera view mirrored or not, default to `true`
   */
  mirrored?: boolean;
  onCaptureSuccess: () => void;
  /**
   * The camera with, default to `430`
   */
  width?: number;
}

/**
 * -----------------------------------------------------------------------------
 * This renders a simple camera element that loads a device camera.
 *
 * NOTE: Currently, this is a temporary implementation that only checks if the
 * camera is working and automatically captures a screenshot after a given wait
 * period, but does not use the image.
 */
export function SimpleAutoBackCamera({
  cameraAccessErrorMessage,
  className = '',
  classNameError = '',
  facingMode = 'front',
  height = 932,
  mirrored = true,
  onCaptureSuccess,
  width = 430,
}: SimpleAutoCameraProps) {
  const cameraStyles: CSSProperties = {
    width: `${width}px`,
    height: `${height}px`,
    position: 'fixed',
    right: 0,
    bottom: 0,
    minWidth: '100%',
    minHeight: '100%',
  };

  /**
   * Duration to wait in (seconds) before capturing with camera.
   */
  const DEFAULT_WAIT_TO_CAPTURE = 5;

  const webcamRef = useRef<Webcam | null>(null);
  const [hasAccessError, setHasAccessError] = useState(false);
  const [isCameraLoading, setIsCameraLoading] = useState(true);

  const handleCameraLoaded = () => {
    setIsCameraLoading(false);
  };

  /**
   * Give device access to the front camera and continue evaluation.
   */
  const handleOnAllowAccess = () => {
    setHasAccessError(false);
    setIsCameraLoading(false);
  };

  /**
   * Give device access to the front camera and continue evaluation.
   */
  const handleOnAllowDenied = () => {
    setHasAccessError(true);
  };

  const handleRequestCameraAccess = () => {
    requestCameraPermission({
      onGrantedCb: handleOnAllowAccess,
      onErrorCb: handleOnAllowDenied,
    });
  };

  const handleCaptureImage = () => {
    const camera = webcamRef.current;

    if (!camera) return;

    const imageSrc = camera.getScreenshot();

    if (imageSrc) {
      onCaptureSuccess();
    }

    setHasAccessError(!imageSrc);
  };

  useEffect(() => {
    handleRequestCameraAccess();

    const timeoutId = setTimeout(() => {
      handleCaptureImage();
    }, DEFAULT_WAIT_TO_CAPTURE * 1000);

    return () => {
      clearTimeout(timeoutId);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webcamRef.current]);

  const errorClass = hasAccessError
    ? 'opacity-100 z-20'
    : 'opacity-0 z-10 pointer-events-none';

  /**
   * Whether to render the front facing selfie camera or the environment/back camera.
   */
  const cameraFacingMode = facingMode === 'front' ? 'user' : 'environment';

  return (
    <div className="relative h-full w-full border-2 border-gray-300">
      <button
        className={clsx(
          'absolute left-0 right-0 top-0 z-20 flex h-full w-full flex-col items-center',
          'justify-center border-4 border-red-50 bg-red-100',
          classNameError,
          errorClass
        )}
        onClick={handleRequestCameraAccess}
        type="button"
      >
        <p className="w-10/12 text-center text-sm text-red-800">
          <Text
            text={
              cameraAccessErrorMessage ||
              translations.camera.cameraAccessErrorMessage
            }
          />
        </p>
      </button>

      <Webcam
        ref={webcamRef}
        className={clsx('z-50 bg-white', className)}
        height={height}
        mirrored={mirrored}
        onUserMedia={handleCameraLoaded}
        screenshotFormat="image/jpeg"
        style={cameraStyles}
        videoConstraints={{
          width,
          height,
          facingMode: cameraFacingMode,
        }}
        width={width}
      />

      {isCameraLoading ? <CameraLoader className="top-0 z-[100]" /> : null}
    </div>
  );
}
